import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import SortPopover, { getDirectionLabels } from './sortPopover';
import { SortTriggerButton } from './sortPopover.styled';
import { ChevronDown } from '../icons';
import { Text } from '../Ant';
import { IActiveSortState, ISortOption } from '../../interface/common';
import { Flex } from 'antd';

interface SortSelectorProperties {
  options: ISortOption[];
  activeSort: IActiveSortState;
  onApply?: (sort: IActiveSortState) => void;
  onCloseModal?: () => void;
  modalLabel?: string;
  disabled?: boolean;
}

const SortSelector = ({
  options,
  activeSort,
  onApply,
  onCloseModal,
  modalLabel,
  disabled = false,
}: SortSelectorProperties) => {
  const [visible, setVisible] = useState(false);
  const { t } = useTranslation();

  useEffect(() => {
    if (disabled) {
      setVisible(false);
    }
  }, [disabled]);

  if (options.length === 0) return null;

  const isPopoverVisible = !disabled && visible;
  const activeOption = options.find((option) => option.field === activeSort.field);
  const activeLabels = activeOption
    ? getDirectionLabels(activeOption.type, t)
    : { asc: '', desc: '' };
  const activeDirectionLabel =
    activeSort.direction === 'ASC' ? activeLabels.asc : activeLabels.desc;
  const currentSortLabel = activeOption ? `${activeOption.label} : ${activeDirectionLabel}` : '';

  return (
    <Flex gap={5} align="center">
      <Text
        data-testid="sort-trigger-label"
        $fontsize="12px"
        $color="description"
        $lineheight="18px"
        className="ta-left white-space-nowrap"
        $width="auto"
      >
        {t('common:sorting.sortedBy')} :
      </Text>
      <SortPopover
        options={options}
        activeSort={activeSort}
        onApply={onApply}
        onCloseModal={onCloseModal}
        modalLabel={modalLabel}
        visible={isPopoverVisible}
        onOpenChange={(open) => setVisible(disabled ? false : open)}
      >
        <SortTriggerButton
          type="button"
          className={`sort-trigger ${isPopoverVisible ? 'open' : ''}`}
          aria-expanded={isPopoverVisible}
          aria-haspopup="dialog"
          data-testid="sort-trigger"
          disabled={disabled}
        >
          <Flex gap={2} align="center" className="overflow-hidden">
            <Text
              data-testid="sort-trigger-value"
              $fontsize="12px"
              $color="primary"
              $weight={500}
              $lineheight="16px"
              className="ta-left lineclamp-1"
              $width="auto"
            >
              {currentSortLabel}
            </Text>
            {!disabled && (
              <span
                aria-hidden="true"
                data-testid="sort-trigger-icon"
                className="d-flex items-center flex-shrink-0"
              >
                <ChevronDown className="fz-14 text-clr-general-description" />
              </span>
            )}
          </Flex>
        </SortTriggerButton>
      </SortPopover>
    </Flex>
  );
};

export default SortSelector;
